import { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { usePrivate } from '@/components/private';
import { ADMIN_ROLES, UserRole } from '@/lib/types';

/**
 * Resolves the user's organisation from the `[orgName]` URL segment and
 * exposes a few common derived flags (membership, admin status, role ids).
 *
 * Centralising this here keeps registry components from re-implementing the
 * same `userOrganisations.find(...)` lookup and per-org admin checks.
 *
 * Permission flags are derived from the full effective role list
 * (`roleIds`) returned by the backend rather than the single primary
 * `roleId`. This matters for users who hold multiple roles in an org —
 * e.g. a wildcard Breedlogic Admin who is also Society Admin: the
 * primary id is the lowest one, so checking just `roleId` would hide
 * the higher-privilege role.
 *
 * - `isAdmin` — user has any per-org admin role (SocietyAdmin or Breedlogic Admin).
 * - `isBreedlogicAdmin` — user has the elevated Breedlogic Admin role.
 */
export function useCurrentOrg() {
  const { orgName } = useParams<{ orgName: string }>();
  const { userOrganisations } = usePrivate();

  return useMemo(() => {
    const isLoading = userOrganisations === undefined;
    const org = userOrganisations?.find(
      (o) => o.organisation?.name === orgName,
    );

    // Prefer the full role list when the backend provides it; fall back to
    // the single `roleId` for older payloads.
    const roleIds: number[] =
      org?.roleIds && org.roleIds.length > 0
        ? org.roleIds
        : org?.roleId != null
          ? [org.roleId]
          : [];

    return {
      org,
      orgName,
      roleId: org?.roleId,
      roleIds,
      isAdmin: roleIds.some((id) => ADMIN_ROLES.includes(id as UserRole)),
      isBreedlogicAdmin: roleIds.includes(UserRole.Admin),
      isMember: Boolean(org),
      isLoading,
    };
  }, [userOrganisations, orgName]);
}
